import { makeStyles } from '@material-ui/core/styles'
import { DASHBOARD_SURFACE_MUTED } from 'pages/pga-coach/dashboard/dashboard-colors'

const RESULT_BORDER = '#E0E0E0'

const useStyles = makeStyles(theme => ({
  upgradeBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: '#E0EAFF',
    border: '1px solid #C5D4FF',
    borderRadius: 12,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  upgradeCopy: {
    flex: 1,
    minWidth: 0,
  },
  upgradeTitle: {
    fontWeight: 700,
    fontSize: '1.125rem',
    lineHeight: 1.3,
    color: '#00205B',
    marginBottom: theme.spacing(0.75),
  },
  upgradeCaption: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#172024',
  },
  pillButton: {
    borderRadius: 20,
    textTransform: 'none',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  ruleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
  },
  statusGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  editButton: {
    textTransform: 'none',
    fontWeight: 500,
  },
  footerNote: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  approvalTitle: {
    '&&': { paddingBottom: theme.spacing(2) },
  },
  approvalContent: {
    '&&': { paddingTop: 0 },
  },
  dialogHint: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  dialogAlert: {
    marginBottom: theme.spacing(2),
  },
  previewLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#6B7C87',
    marginBottom: theme.spacing(1),
  },
  previewMessage: {
    whiteSpace: 'pre-wrap',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#172024',
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#F4F6F8',
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
  recipients: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(2),
  },
  finePrint: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  finePrintBreak: {
    marginTop: theme.spacing(1),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  mergeFields: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  mergeFieldsNote: {
    marginTop: theme.spacing(2),
  },
  mergeFieldsLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  resetButton: {
    marginRight: 'auto',
    textTransform: 'none',
  },
  resultHeader: {
    display: 'flex',
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    textAlign: 'left',
    color: 'inherit',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.75, 2),
      gap: theme.spacing(1.5),
    },
  },
  resultHeaderExpanded: {
    borderBottom: `1px solid ${RESULT_BORDER}`,
  },
  resultHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    flex: '1 1 0',
    overflow: 'hidden',
  },
  resultHeaderText: {
    minWidth: 0,
    flex: '1 1 auto',
  },
  resultHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '0 0 auto',
  },
  resultIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: '#EBF0F2',
    color: '#172024',
    '& svg, & .MuiSvgIcon-root': {
      width: 16,
      height: 16,
      fontSize: 16,
      color: '#172024',
    },
  },
  resultTitle: {
    margin: 0,
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#172024',
  },
  resultDescription: {
    margin: theme.spacing(0.5, 0, 0),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  resultChevron: {
    flexShrink: 0,
    width: 20,
    height: 20,
    fontSize: 20,
    color: 'rgba(0, 0, 0, 0.38)',
  },
  resultBody: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    padding: theme.spacing(2, 2.5, 2.5),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
      padding: theme.spacing(1.75, 2, 2),
    },
  },
  rateBlock: {
    minWidth: 0,
  },
  rateLabel: {
    margin: 0,
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
    color: '#8A9AA3',
  },
  rateValue: {
    margin: theme.spacing(0.5, 0, 1.25),
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.1,
    color: '#172024',
  },
  rateTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: DASHBOARD_SURFACE_MUTED,
    overflow: 'hidden',
  },
  rateFill: {
    height: '100%',
    borderRadius: 3,
  },
  rateCaption: {
    margin: theme.spacing(1, 0, 0),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  resultEmptyCaption: {
    marginBottom: 0,
  },
  resultFootnote: {
    padding: theme.spacing(1.5, 2.5, 2),
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
}))

export default useStyles
